import React from 'react';

export type AppBackgroundVariant = 'full' | 'flat' | 'landing';

/**
 * `flat` — solid canvas only (max LCP safety).
 * `landing` — atmosphere via cheap radial-gradients (no CSS filter blur).
 * `full` — brand glow orbs for inner marketing / product shells.
 */
const AppBackground: React.FC<{ variant?: AppBackgroundVariant }> = ({ variant = 'full' }) => {
  if (variant === 'flat') {
    return <div className="fixed inset-0 bg-gray-900 -z-20" />;
  }

  if (variant === 'landing') {
    return (
      <>
        <div className="fixed inset-0 bg-gray-900 -z-20" />
        {/* Paint-cheap mesh — gradients only, no backdrop-filter / blur() */}
        <div
          className="fixed inset-0 -z-10 pointer-events-none"
          aria-hidden
          style={{
            backgroundImage: [
              'radial-gradient(ellipse 70% 55% at 15% 10%, rgba(22,163,74,0.22), transparent 55%)',
              'radial-gradient(ellipse 55% 45% at 90% 85%, rgba(5,150,105,0.14), transparent 50%)',
              'radial-gradient(ellipse 40% 35% at 70% 20%, rgba(74,222,128,0.06), transparent 60%)',
            ].join(', '),
          }}
        />
        <div
          className="fixed inset-0 opacity-[0.035] pointer-events-none bg-repeat -z-10"
          aria-hidden
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
      </>
    );
  }

  return (
    <>
      <div className="fixed inset-0 bg-gray-900 -z-20" />

      {/* Brand green glow (top-left) — blur capped for paint cost */}
      <div className="fixed top-0 left-0 sm:left-1/4 w-[500px] h-[500px] bg-primary/20 rounded-full blur-[80px] pointer-events-none mix-blend-screen -z-10" />

      {/* Softer emerald glow (bottom-right) */}
      <div className="fixed bottom-0 right-0 sm:right-1/4 w-[500px] h-[500px] bg-emerald-600/10 rounded-full blur-[80px] pointer-events-none mix-blend-screen -z-10" />

      <div className="fixed inset-0 opacity-[0.02] pointer-events-none bg-repeat -z-10"
           style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}
      />
    </>
  );
};

export default AppBackground;
